import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tokenExchangeInProgress = useRef(false);
  const codeProcessed = useRef(false);

  const getCognitoDomain = useCallback(() => {
    const domainPrefix = process.env.REACT_APP_USER_POOL_ID.replace('_', '');
    return `https://${domainPrefix}.auth.us-east-2.amazoncognito.com`;
  }, []);

  const getRedirectUri = useCallback(() => {
    return process.env.REACT_APP_REDIRECT_SIGNIN;
  }, []);

  const generateRandomString = useCallback((length) => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const randomValues = new Uint8Array(length);
    window.crypto.getRandomValues(randomValues);
    for (let i = 0; i < length; i++) {
      result += charset[randomValues[i] % charset.length];
    }
    return result;
  }, []);

  const generateCodeChallenge = useCallback(async (codeVerifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
    return base64
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }, []);

  const exchangeCodeForToken = useCallback(async (code, state) => {
    try {
      // Validate state parameter
      const savedState = sessionStorage.getItem('cognito_state');
      const savedCodeVerifier = sessionStorage.getItem('cognito_code_verifier');
      
      if (!savedState || state !== savedState) {
        throw new Error('Invalid state parameter');
      }

      const tokenEndpoint = `${getCognitoDomain()}/oauth2/token`;
      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: process.env.REACT_APP_USER_POOL_CLIENT_ID,
        code,
        redirect_uri: getRedirectUri(),
        code_verifier: savedCodeVerifier
      });

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });

      if (!response.ok) {
        throw new Error('Token exchange failed');
      }

      const data = await response.json();
      
      // Store tokens in localStorage
      localStorage.setItem('idToken', data.id_token);
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      localStorage.setItem('tokenExpiry', (Date.now() + data.expires_in * 1000).toString());

      // Clean up session storage
      sessionStorage.removeItem('cognito_state');
      sessionStorage.removeItem('cognito_code_verifier');

      // Get user info from ID token
      const userInfo = parseJwt(data.id_token);
      setCurrentUser(userInfo);
      
      logger.debug('Token exchange successful', {
        email: userInfo.email,
        groups: userInfo['cognito:groups'] || []
      });

      return data;
    } catch (error) {
      logger.error('Token exchange failed:', error);
      throw error;
    }
  }, [getCognitoDomain, getRedirectUri]);

  const clearTokens = useCallback(() => {
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    setCurrentUser(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      // Track logout before clearing user data
      const currentUserEmail = currentUser?.email;
      if (currentUserEmail) {
        // Log the logout event using the unauthorized endpoint since we're about to clear tokens
        await fetch('/api/unauthorized-log', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            level: 'info',
            message: 'User logged out',
            metadata: {
              userEmail: currentUserEmail,
              timestamp: new Date().toISOString(),
              groups: currentUser['cognito:groups'] || []
            }
          })
        });
      }

      // Clear all tokens from localStorage
      clearTokens();
      
      // Redirect to Cognito logout with the correct parameters
      const logoutUrl = `${getCognitoDomain()}/logout?` +
        `client_id=${process.env.REACT_APP_USER_POOL_CLIENT_ID}&` +
        `response_type=code&` +
        `logout_uri=${encodeURIComponent(process.env.REACT_APP_REDIRECT_SIGNOUT)}`;
      window.location.href = logoutUrl;
    } catch (error) {
      logger.error('Error during logout:', error);
      // Still proceed with logout even if logging fails
      clearTokens();
      const logoutUrl = `${getCognitoDomain()}/logout?` +
        `client_id=${process.env.REACT_APP_USER_POOL_CLIENT_ID}&` +
        `response_type=code&` +
        `logout_uri=${encodeURIComponent(process.env.REACT_APP_REDIRECT_SIGNOUT)}`;
      window.location.href = logoutUrl;
    }
  }, [getCognitoDomain, clearTokens, currentUser]);

  const refreshTokens = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const tokenEndpoint = `${getCognitoDomain()}/oauth2/token`;
      const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: process.env.REACT_APP_USER_POOL_CLIENT_ID,
        refresh_token: refreshToken
      });

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      // Update tokens in localStorage
      localStorage.setItem('idToken', data.id_token);
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('tokenExpiry', (Date.now() + data.expires_in * 1000).toString());

      // Get user info from new ID token
      const userInfo = parseJwt(data.id_token);
      setCurrentUser(userInfo);
      
      logger.info('Token refresh successful', {
        email: userInfo.email,
        groups: userInfo['cognito:groups'] || []
      });

      return data;
    } catch (error) {
      logger.error('Token refresh failed:', error);
      // Clear all tokens if refresh fails
      clearTokens();
      throw error;
    }
  }, [getCognitoDomain, clearTokens]);

  const login = useCallback(async () => {
    try {
      const domain = getCognitoDomain();
      const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
      const redirectUri = getRedirectUri();
      const state = generateRandomString(32);
      const codeVerifier = generateRandomString(128);
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store state and PKCE values in session storage
      sessionStorage.setItem('cognito_state', state);
      sessionStorage.setItem('cognito_code_verifier', codeVerifier);

      // Construct the authorization URL according to Cognito's specifications
      const authUrl = `${domain}/oauth2/authorize?` +
        `response_type=code&` +
        `client_id=${clientId}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `state=${state}&` +
        `code_challenge=${codeChallenge}&` +
        `code_challenge_method=S256`;

      // Redirect to Cognito's authorization endpoint
      window.location.href = authUrl;
    } catch (error) {
      setError(error.message);
      logger.error('Error during login', {
        component: 'cognito',
        error: error.message
      });
    }
  }, [getCognitoDomain, getRedirectUri, generateRandomString, generateCodeChallenge]);

  // Auto refresh tokens before they expire
  useEffect(() => {
    if (!currentUser) return;

    const tokenExpiry = parseInt(localStorage.getItem('tokenExpiry'));
    if (!tokenExpiry) return;

    const timeToExpiry = tokenExpiry - Date.now();
    if (timeToExpiry <= 0) {
      refreshTokens().catch(() => clearTokens());
      return;
    }

    // Refresh 5 minutes before expiry
    const refreshTime = Math.max(timeToExpiry - 5 * 60 * 1000, 0);
    const refreshTimer = setTimeout(() => {
      refreshTokens().catch(() => clearTokens());
    }, refreshTime);

    return () => clearTimeout(refreshTimer);
  }, [currentUser, refreshTokens, clearTokens]);

  useEffect(() => {
    async function checkAuth() {
      try {
        // First check if we have stored tokens
        const storedTokens = localStorage.getItem('idToken');
        if (storedTokens) {
          const idToken = localStorage.getItem('idToken');
          const accessToken = localStorage.getItem('accessToken');
          const refreshToken = localStorage.getItem('refreshToken');
          const tokenExpiry = parseInt(localStorage.getItem('tokenExpiry'));
          
          // Check Cognito session status
          try {
            const domain = getCognitoDomain();
            const response = await fetch(`${domain}/oauth2/userInfo`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`
              }
            });
            
            if (!response.ok) {
              // Cognito session is invalid, clear tokens and redirect to login
              clearTokens();
              login();
              return;
            }
          } catch (error) {
            // Error checking Cognito session, clear tokens and redirect to login
            clearTokens();
            login();
            return;
          }

          // Session is valid, proceed with normal token checks
          const timeToExpiry = tokenExpiry - Date.now();
          if (timeToExpiry <= 0) {
            await refreshTokens();
          }
          const userInfo = parseJwt(idToken);
          setCurrentUser({
            ...userInfo,
            tokens: {
              idToken,
              accessToken,
              refreshToken
            }
          });
          setLoading(false);
          return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        // If we've already processed this code or there's no code, return early
        if (!code || codeProcessed.current) {
          setLoading(false);
          return;
        }

        // If we're already processing a token exchange, return early
        if (tokenExchangeInProgress.current) {
          console.log('Token exchange already in progress, skipping...');
          return;
        }

        try {
          tokenExchangeInProgress.current = true;
          codeProcessed.current = true;

          if (error || errorDescription) {
            throw new Error(`Authentication error: ${error} - ${errorDescription}`);
          }

          // Clean up URL immediately to prevent reuse
          window.history.replaceState({}, document.title, window.location.pathname);

          console.log('Starting token exchange...');
          const tokens = await exchangeCodeForToken(code, state);
          console.log('Successfully exchanged code for tokens');
          
          // Get user info from ID token
          const userInfo = parseJwt(tokens.id_token);
          setCurrentUser({
            ...userInfo,
            tokens
          });
        } catch (error) {
          logger.error('Error during authentication check', {
            component: 'cognito',
            error: error.message
          });
          setError(error.message);
        } finally {
          tokenExchangeInProgress.current = false;
          setLoading(false);
        }
      } catch (error) {
        logger.error('Error during auth check', {
          component: 'cognito',
          error: error.message
        });
        setLoading(false);
      }
    }

    checkAuth();

    // Cleanup function
    return () => {
      tokenExchangeInProgress.current = false;
      codeProcessed.current = false;
    };
  }, [exchangeCodeForToken, refreshTokens]);

  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    refreshTokens
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Helper function to parse JWT token
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}
